import { EventType } from "@/analytics/analytics-events";
import {
  CancelAnnotationCreationEventProperties,
  CreateAnnotationEventProperties,
} from "@/analytics/annotation-events-properties";
import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import {
  CreateAnnotationForm,
  CreateAnnotationFormProps,
} from "@/components/ui/annotations/create-annotation-form";
import { useCurrentScene } from "@/modes/mode-data-context";
import { useIsPanoWithDepthAndReady } from "@/modes/walk-mode/walk-mode-hooks";
import {
  isFromSheetObject,
  isPanoObject,
  UnknownObject,
} from "@/object-cache-type-guard";
import { setHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { selectActiveElementId } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { deactivateTool, setAnnotationExpansion } from "@/store/ui/ui-slice";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import { useUpdateVisibilityDistance } from "@/utils/use-update-visibility-distance";
import {
  CreateRectangleAnnotation,
  selectIElementWorldPosition,
  useImmediateControlsLock,
  useOverrideCursor,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID, TypedEvent } from "@faro-lotv/foundation";
import { IElementTypeHint } from "@faro-lotv/ielement-types";
import { sphere3DToEquirectangular2D } from "@faro-lotv/lotv";
import { isIElementVideoModeCopy } from "@faro-lotv/project-source";
import { Dialog } from "@mui/material";
import { useThree } from "@react-three/fiber";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
} from "react";
import { Mesh, Vector3 } from "three";
import {
  isPointAnnotationData,
  PointAnnotationData,
} from "../annotation-mutation-utils";
import { ToolCallback, ToolControlsRef } from "../tool-controls-interface";
import { useLensCursor } from "../use-lens-cursor";
import { useZoomLensPicking } from "../use-zoom-lens-picking";
import {
  ImagePointAnnotationData,
  useCreateImagePointAnnotation,
} from "./use-create-image-point-annotation";
import { useCreateModel3dAnnotation } from "./use-create-model-3d-annotation";
import {
  cameraToAnnotationCameraParams,
  useCreateSinglePointAnnotation,
} from "./use-create-single-point-annotation";

type AnnotationToCreate = PointAnnotationData | Mesh | ImagePointAnnotationData;

type AnnotationPickerProps = {
  /** True if this tool is the active one */
  isActive: boolean;

  /** List of active models in the scene that will send events to the tool */
  activeModels: UnknownObject[] | null;

  /** Callback issued when the hovered point changes */
  currentPointChanged?(point: Vector3 | undefined): void;

  /** Optional signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens?: TypedEvent<GUID>;
};

/** @returns the tool to create new annotations */
export const AnnotationPicker = forwardRef<
  ToolControlsRef,
  AnnotationPickerProps
>(function AnnotationPicker(
  { isActive, activeModels, currentPointChanged, onValidClickInZoomLens },
  ref,
): JSX.Element | null {
  const dispatch = useAppDispatch();

  // Whether the annotation is being created while viewing a VideoMode copy
  const { referenceElement } = useCurrentScene();
  const inVideoModeCopy =
    !!referenceElement && isIElementVideoModeCopy(referenceElement);

  // Reset flag each time the component is enabled
  useEffect(() => {
    if (isActive) {
      dispatch(setHasCreatedNewAnnotation(false));
    }
  }, [dispatch, isActive]);

  const [annotation, setAnnotation] = useState<AnnotationToCreate>();
  // The r3f click and the zoom lens signal both fire for one pointer up, and React state
  // is not updated in between, so the ref is what prevents starting two annotations
  const annotationRef = useRef<AnnotationToCreate>(undefined);
  const startAnnotation = useCallback((toCreate?: AnnotationToCreate) => {
    annotationRef.current = toCreate;
    setAnnotation(toCreate);
  }, []);

  const isZoomLensOn = useAppSelector(selectZoomLensActive);
  // The lens picks on the point cloud behind the 360, which the annotation would not record otherwise
  const acquisitionPanoId = useAppSelector(selectActiveElementId);

  // Function to start a new annotation, either from a point picked in the scene or from a click in the zoom lens.
  // The picked point is the last one hovered, not the one under the cursor at click time.
  const startPointAnnotation = useCallback(
    (
      point: Vector3,
      parentId: GUID,
      type: PointAnnotationData["type"] = IElementTypeHint.spaceAnnotation,
    ) => {
      if (annotationRef.current) return;

      Analytics.track<CreateAnnotationEventProperties>(
        EventType.createAnnotation,
        {
          shape: "point",
          inVideoModeCopy,
          via: "scene",
        },
      );

      startAnnotation({
        points: [point.toArray()],
        parentId,
        type,
        acquisitionPanoId: isZoomLensOn ? acquisitionPanoId : undefined,
      } satisfies PointAnnotationData);
    },
    [inVideoModeCopy, isZoomLensOn, acquisitionPanoId, startAnnotation],
  );

  // Get the hover handler from the zoom lens, which will call startPointAnnotation when a valid click is signaled
  const pointHovered = useZoomLensPicking({
    isActive,
    currentPointChanged,
    onValidClickInZoomLens,
    onPointPicked: startPointAnnotation,
  });

  // Override the cursor to a crosshair when the tool is active and the zoom lens is not on, and to the lens cursor when it is on
  useOverrideCursor("crosshair", isActive && !annotation && !isZoomLensOn);
  useLensCursor(isActive && !annotation && isZoomLensOn);

  const [isUserDragging, setIsUserDragging] = useState(false);
  const pointClicked = useCallback<ToolCallback>(
    (ev, iElementId) => {
      if (isUserDragging) return;
      ev.stopPropagation();

      startPointAnnotation(
        ev.point,
        iElementId,
        isFromSheetObject(ev.object)
          ? IElementTypeHint.mapAnnotation
          : IElementTypeHint.spaceAnnotation,
      );
    },
    [isUserDragging, startPointAnnotation],
  );

  useImperativeHandle(ref, () => ({
    pointClicked,
    pointHovered,
  }));

  const panoElement = activeModels?.find(isPanoObject)?.iElement;
  const panoMatrixWorldInverse = activeModels
    ?.find(isPanoObject)
    ?.matrixWorld.clone()
    .invert();

  const { isPanoWithDepth } = useIsPanoWithDepthAndReady(panoElement);

  const camera = useThree((s) => s.camera);
  const store = useAppStore();

  const createSinglePointAnnotation = useCreateSinglePointAnnotation();
  const createModel3dAnnotation = useCreateModel3dAnnotation();
  const updateVisibilityDistance = useUpdateVisibilityDistance();
  const createImagePointAnnotation = useCreateImagePointAnnotation();

  const saveAnnotation = useCallback<CreateAnnotationFormProps["onSave"]>(
    async (details) => {
      assert(annotation, "Unable to save an undefined annotation");

      let markupId: GUID;

      if (annotation instanceof Mesh) {
        assert(
          panoElement,
          "A pano is required to create a Model3D Annotation",
        );
        markupId = await createModel3dAnnotation(
          panoElement,
          annotation,
          details,
          cameraToAnnotationCameraParams(camera),
        );
      } else if (isPointAnnotationData(annotation)) {
        markupId = await createSinglePointAnnotation(
          annotation,
          details,
          cameraToAnnotationCameraParams(camera),
        );
      } else {
        markupId = await createImagePointAnnotation(annotation, details);
      }

      // Auto expand the new annotation
      dispatch(setAnnotationExpansion({ id: markupId, expanded: true }));

      // Ensure the new generated annotation is visible
      const markupPosition = selectIElementWorldPosition(markupId)(
        store.getState(),
      );
      updateVisibilityDistance(camera, new Vector3().fromArray(markupPosition));
      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.annotations,
          visibility: true,
        }),
      );

      dispatch(setHasCreatedNewAnnotation(true));
      startAnnotation(undefined);
      dispatch(deactivateTool());
    },
    [
      annotation,
      camera,
      createModel3dAnnotation,
      createSinglePointAnnotation,
      createImagePointAnnotation,
      dispatch,
      panoElement,
      startAnnotation,
      store,
      updateVisibilityDistance,
    ],
  );

  const closeDialog = useCallback(
    () => startAnnotation(undefined),
    [startAnnotation],
  );
  const { setContent } = useUiOverlayContext();

  useEffect(() => {
    if (!annotation) return;

    setContent(
      <Dialog
        open
        slotProps={{
          paper: {
            sx: { p: 1, background: "transparent" },
            elevation: 0,
          },
        }}
      >
        <CreateAnnotationForm
          onClose={closeDialog}
          onSave={saveAnnotation}
          allowExternalMarkups
        />
      </Dialog>,
    );

    return () => setContent(undefined);
  }, [annotation, closeDialog, saveAnnotation, setContent]);

  // This callback does  not depend on the 'pano' object, but only on the 'panoElement' and on the 'panoMatrixWorldInverse'
  // matrix. If it depended on 'pano', there would be a problem where all panos ever mounted by this component
  // are still kept alive by the 'boxes' the callbacks create when lambda-capturing the variables.
  // Dropping the old function from the hook does not erase the pointer that an older box already wrote.
  // And because the callbacks have different dependencies, there is always one reused callback holding
  // the previous box. That is why the chain never breaks.
  const onPointAnnotationCreated = useCallback(
    (point: Vector3) => {
      if (!panoElement || !panoMatrixWorldInverse) return;
      // Converting point from global to local coordinates to the pano
      point.applyMatrix4(panoMatrixWorldInverse);
      // Converting the local point to the pixel in the pano image
      const pixel = sphere3DToEquirectangular2D(
        point,
        panoElement.pixelWidth,
        panoElement.pixelHeight,
      );
      Analytics.track<CreateAnnotationEventProperties>(
        EventType.createAnnotation,
        { shape: "pano pixel", inVideoModeCopy, via: "scene" },
      );
      // Creating an annotation to the given pano pixel
      startAnnotation({
        pixelPosition: {
          x: Math.round(pixel.x),
          y: Math.round(pixel.y),
        },
        parentId: panoElement.id,
      } satisfies ImagePointAnnotationData);
    },
    [panoElement, panoMatrixWorldInverse, inVideoModeCopy, startAnnotation],
  );

  const onRectangleAnnotationCreated = useCallback(
    (annotation: Mesh) => {
      Analytics.track<CreateAnnotationEventProperties>(
        EventType.createAnnotation,
        { shape: "rectangle", inVideoModeCopy, via: "scene" },
      );
      startAnnotation(annotation);
    },
    [inVideoModeCopy, startAnnotation],
  );

  const onAnnotationCanceled = useCallback(
    () =>
      Analytics.track<CancelAnnotationCreationEventProperties>(
        EventType.cancelAnnotationCreation,
        { via: "escape key" },
      ),
    [],
  );

  const canTakePixelAnnotations = !isPanoWithDepth;

  const { lock, unlock } = useImmediateControlsLock();

  const onDragStart = useCallback(() => {
    lock();
    setIsUserDragging(true);
  }, [lock]);

  const onDragEnd = useCallback(() => {
    unlock();
    setIsUserDragging(false);
  }, [unlock]);

  return (
    <>
      {isActive && panoElement && (
        <CreateRectangleAnnotation
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onCreated={onRectangleAnnotationCreated}
          onCancelled={onAnnotationCanceled}
          onPointClicked={
            canTakePixelAnnotations ? onPointAnnotationCreated : undefined
          }
        />
      )}
      {annotation instanceof Mesh && <primitive object={annotation} />}
    </>
  );
});
